import React from 'react';
import { FormattedMessage, IntlShape } from 'react-intl';
import { ColumnDef } from '@tanstack/react-table';
import { generatePath } from 'react-router-dom';
import { GetCustomersResponseSingleItem } from '../../setup/apiTypes/customers';
import { ButtonMode } from '../../components/atoms/Button/enums';
import ButtonLink from '../../components/atoms/Button/buttonLink';
import routes from '../../setup/consts/routes';
import {
  StyledActionCell,
  StyledCityPill,
  StyledCustomerAvatar,
  StyledCustomerCell,
  StyledCustomerInfo,
  StyledCustomerName,
  StyledCustomerSubtitle,
  StyledOrderCount,
  StyledPhoneCell,
  StyledValueCell,
} from './styles';

const getInitials = (name: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const customersTableFakeData: GetCustomersResponseSingleItem[] = [
  {
    id: Math.random().toString(),
    name: 'Abas Hanoon',
    phone: '96403458981',
    lastOrderDate: new Date('01/02/2020'),
    city: 'Baghdad',
    totalOrders: 320,
    value: 260000,
    rating: 3.5,
  },
];

export const customersTableColumns = (
  intl: IntlShape
): ColumnDef<GetCustomersResponseSingleItem>[] => [
  {
    header: intl.formatMessage({
      id: 'customers.customerName',
      defaultMessage: 'Customer',
    }),
    accessorKey: 'name',
    cell: ({ row: { original } }) => (
      <StyledCustomerCell>
        <StyledCustomerAvatar name={original.name || ''}>
          {getInitials(original.name || '')}
        </StyledCustomerAvatar>
        <StyledCustomerInfo>
          <StyledCustomerName>
            {original.name || <em style={{ opacity: 0.4 }}>Unnamed</em>}
          </StyledCustomerName>
          {original.lastOrderDate && (
            <StyledCustomerSubtitle>
              Last order&nbsp;·&nbsp;
              {new Date(original.lastOrderDate).toLocaleDateString()}
            </StyledCustomerSubtitle>
          )}
        </StyledCustomerInfo>
      </StyledCustomerCell>
    ),
  },
  {
    header: intl.formatMessage({
      id: 'customers.customerPhone',
      defaultMessage: 'Phone',
    }),
    accessorKey: 'phone',
    cell: ({ cell: { getValue } }) => (
      <StyledPhoneCell>{(getValue() as string) || '—'}</StyledPhoneCell>
    ),
  },
  {
    header: intl.formatMessage({
      id: 'customers.city',
      defaultMessage: 'City',
    }),
    accessorKey: 'city',
    cell: ({ cell: { getValue } }) => {
      const city = getValue() as string;
      if (!city) return <span style={{ opacity: 0.4 }}>—</span>;
      return <StyledCityPill>{city}</StyledCityPill>;
    },
  },
  {
    header: intl.formatMessage({
      id: 'customers.totalOrders',
      defaultMessage: 'Orders',
    }),
    accessorKey: 'totalOrders',
    cell: ({ cell: { getValue } }) => {
      const v = (getValue() as number) ?? 0;
      return (
        <StyledOrderCount>
          <strong>{v.toLocaleString()}</strong>
          <span>{v === 1 ? 'order' : 'orders'}</span>
        </StyledOrderCount>
      );
    },
  },
  {
    header: intl.formatMessage({
      id: 'customers.valueIQD',
      defaultMessage: 'Value',
    }),
    accessorKey: 'totalPrice',
    cell: ({ cell: { getValue } }) => {
      const v = (getValue() as number) ?? 0;
      return (
        <StyledValueCell>
          <strong>{v.toLocaleString()}</strong>
          <span>IQD</span>
        </StyledValueCell>
      );
    },
  },
  {
    header: '',
    id: 'action',
    size: 60,
    cell: ({ row: { original } }) => (
      <StyledActionCell>
        <ButtonLink
          mode={ButtonMode.Secondary}
          onClick={(e) => {
            e.stopPropagation();
          }}
          to={generatePath(routes.PLACE_CUSTOMER_ORDER, {
            customerId: original.id,
          })}
        >
          <FormattedMessage
            id='customers.createOrder'
            defaultMessage='Create order'
          />
        </ButtonLink>
      </StyledActionCell>
    ),
  },
];
